<template src="./template.html"></template>

<script>
import { initGoogleSignIn, renderGoogleButton, scheduleSilentRenew } from '@/plugins/googleAuth.js'
import logoMark from '@/assets/images/sr-logo.png'

export default {
    name: 'Login',
    data() {
        return {
            configError: false,
            logoMark,
        }
    },
    async mounted() {
        const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID
        if (!clientId) {
            this.configError = true
            this.alert({ msg: 'Google 로그인이 구성되지 않았습니다. 관리자에게 문의하세요.' })
            return
        }
        try {
            await initGoogleSignIn({ clientId, onCredential: this.onGoogleCredential })
            await renderGoogleButton(this.$refs.googleBtn, {
                theme: 'outline',
                size: 'large',
                shape: 'pill',
                text: 'signin_with',
                logo_alignment: 'center',
                width: 300,
            })
        } catch (e) {
            this.alert({ msg: 'Google 로그인을 불러오지 못했습니다. 새로고침 해주세요.' })
        }
    },
    methods: {
        onGoogleCredential(idToken) {
            if (!idToken) return
            this.loadingShow()
            this.$store.dispatch('GOOGLE_LOGIN', { idToken })
            .then(result => {
                if (result.role === 'admin') {
                    localStorage.setItem('userInfo', JSON.stringify(result))
                    this.$store.dispatch('updateUserInfo')
                    scheduleSilentRenew({
                        clientId: process.env.VUE_APP_GOOGLE_CLIENT_ID,
                        token: result.token,
                        onCredential: this.onGoogleCredential,
                    })
                    this.$router.push('/member/')
                } else {
                    this.alert({ msg: '관리자 계정만 입장하실 수 있습니다.' })
                }
                this.loadingHide()
            })
            .catch(error => {
                this.loadingHide()
                const errResponse = error.response
                const code = errResponse && errResponse.data && errResponse.data.code
                const status = errResponse && errResponse.status
                if (status === 404 || code === 50) {
                    this.alert({ msg: '등록된 관리자 계정이 없습니다. 관리자에게 문의하세요.' })
                } else if (status === 403 || code === 1) {
                    this.alert({ msg: '관리자 권한이 없거나 허용되지 않은 계정입니다.' })
                } else {
                    this.alert({ msg: '관리자에게 문의하여 주십시오' })
                }
            })
        },
    },
}
</script>

<style scoped>
/* ── Admin login: dark, music-native, content-first ──────────────────────── */
.login-screen {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    overflow: auto;
    /* Soft light canvas, faintly tinted toward the brand blue-purple. */
    background: #f1f2f9;
    font-family: 'Montserrat', sans-serif;
}

.login-panel {
    width: 100%;
    max-width: 372px;
    padding: 48px 40px 36px;
    border: 1px solid rgba(16, 22, 58, 0.07);
    border-radius: 20px;
    /* Clean white surface lifted off the tinted canvas. */
    background: #ffffff;
    box-shadow: 0 28px 70px -28px rgba(28, 33, 80, 0.28);
    text-align: left;
}

.login-mark {
    width: 42px;
    height: 42px;
    display: block;
    border-radius: 11px;
    margin-bottom: 28px;
}

.login-eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #6a72ad;
    margin-bottom: 12px;
}

.login-wordmark {
    margin: 0 0 18px;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.18;
    letter-spacing: -0.01em;
    color: #1b2150;
}

.login-wordmark__sub {
    display: block;
    font-weight: 300;
    color: #818ac0;
    letter-spacing: 0.01em;
}

.login-divider {
    height: 1px;
    margin: 28px 0 26px;
    background: linear-gradient(90deg, rgba(16, 22, 58, 0.14), rgba(16, 22, 58, 0));
}

.login-google {
    display: flex;
    justify-content: center;
    min-height: 44px;
    /* Settle the Google pill onto the white surface. */
    filter: drop-shadow(0 8px 20px rgba(28, 33, 80, 0.14));
}

.login-error {
    font-size: 13px;
    line-height: 1.6;
    color: #c0392b;
    background: rgba(234, 84, 85, 0.08);
    border: 1px solid rgba(234, 84, 85, 0.24);
    border-radius: 12px;
    padding: 14px 16px;
}

.login-secured {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 26px;
    font-size: 11.5px;
    letter-spacing: 0.01em;
    color: #8a90b8;
}

.login-secured__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #28c76f;
    box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.16);
    flex: none;
}

/* ── One orchestrated reveal on load ─────────────────────────────────────── */
.login-mark,
.login-eyebrow,
.login-wordmark,
.login-divider,
.login-google,
.login-error,
.login-secured {
    opacity: 0;
    transform: translateY(10px);
    animation: login-rise 0.62s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
.login-mark { animation-delay: 0.04s; }
.login-eyebrow { animation-delay: 0.1s; }
.login-wordmark { animation-delay: 0.16s; }
.login-divider { animation-delay: 0.24s; }
.login-google,
.login-error { animation-delay: 0.36s; }
.login-secured { animation-delay: 0.44s; }

@keyframes login-rise {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 420px) {
    .login-panel {
        padding: 40px 26px 30px;
    }
    .login-wordmark {
        font-size: 23px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .login-mark,
    .login-eyebrow,
    .login-wordmark,
    .login-help,
    .login-divider,
    .login-google,
    .login-error,
    .login-secured {
        animation: none;
        opacity: 1;
        transform: none;
    }
}
</style>
